// S6.5 — type narrowing for permission_request / ask_user_question message
// content shapes. Backend persists these as JSON blobs (see
// backend/src/api/internal-hooks.routes.ts); the union below mirrors the
// production shapes 1:1 so the React layer never has to `as any`.

export type PermissionDecidedBy =
  | 'user'
  | 'allowlist'
  | 'dashboard_timeout'
  | 'agent_died';

// Pending permission_request — the dashboard is waiting on Santi.
export interface PermissionPendingContent {
  request_id: string;
  tool_name: string;
  tool_input: unknown;
  status: 'pending';
  classifier_reason?: string;
}

// Terminal permission_request — appended after the user clicks (or the
// allowlist auto-allowed, or the dashboard timed out).
export interface PermissionTerminalContent {
  request_id: string;
  status: 'allowed' | 'denied' | 'auto_allowed';
  decided_by: PermissionDecidedBy;
  reason?: string;
  // Allowlist hit specifically — useful for the outcome pill ("auto-allowed
  // because of rule X").
  allowlist_rule_id?: string;
  pattern?: string;
  // Tool name/input may be omitted on the terminal write (the pending row
  // still carries them); UI looks them up by request_id when needed.
  tool_name?: string;
  tool_input?: unknown;
}

/**
 * One option offered for an AskUserQuestion.
 *
 * Anthropic ships two shapes over time:
 *   • Legacy: a bare string (the option label).
 *   • Current (2026): an object `{ label, description? }` where
 *     `label` is the chosen value sent back to the agent and
 *     `description` is multi-line founder-facing text.
 *
 * Sprint `askuserquestion-long-hold` (2026-06-02) widened the type and
 * added accessors so consumers stay shape-agnostic. The wire value
 * sent back to the agent is always the `label` string — the answers
 * map (`{ [questionText]: chosenLabel }`) does not change shape.
 */
export type AskUserQuestionOption =
  | string
  | {
      label: string;
      description?: string;
    };

export interface AskUserQuestionEntry {
  question: string;
  // Optional founder-facing caption rendered above the question text
  // when the Anthropic shape includes it.
  header?: string;
  // Per Anthropic spec: questions[] always has options[] (≥1) and
  // multiSelect (boolean). We keep both optional defensively in case the
  // CLI sends an older shape.
  options?: AskUserQuestionOption[];
  multiSelect?: boolean;
}

/** Read the label (the value sent back to the agent) from any option shape. */
export function optionLabel(opt: AskUserQuestionOption): string {
  return typeof opt === 'string' ? opt : opt.label;
}

/** Read the optional description rendered under the label, when present. */
export function optionDescription(opt: AskUserQuestionOption): string | undefined {
  return typeof opt === 'string' ? undefined : opt.description;
}

// Pending ask_user_question — sticky-pinned chip cluster.
export interface AskUserQuestionPendingContent {
  request_id: string;
  tool_name: 'AskUserQuestion';
  questions: AskUserQuestionEntry[];
  status: 'pending';
}

// Type guards. Use these to discriminate before reading specific fields.
export function isPermissionPending(
  c: unknown,
): c is PermissionPendingContent {
  return (
    typeof c === 'object' &&
    c !== null &&
    (c as { status?: unknown }).status === 'pending' &&
    typeof (c as { tool_name?: unknown }).tool_name === 'string'
  );
}

export function isPermissionTerminal(
  c: unknown,
): c is PermissionTerminalContent {
  if (typeof c !== 'object' || c === null) return false;
  const s = (c as { status?: unknown }).status;
  return s === 'allowed' || s === 'denied' || s === 'auto_allowed';
}

export function isAskUserQuestionPending(
  c: unknown,
): c is AskUserQuestionPendingContent {
  return (
    typeof c === 'object' &&
    c !== null &&
    (c as { status?: unknown }).status === 'pending' &&
    Array.isArray((c as { questions?: unknown }).questions)
  );
}

// Pull the request_id off any permission/ask content (pending or terminal).
export function getRequestId(c: unknown): string | null {
  if (typeof c !== 'object' || c === null) return null;
  const id = (c as { request_id?: unknown }).request_id;
  return typeof id === 'string' ? id : null;
}
